// MsbCommissionTransactions.jsx

import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Backdrop,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Fab,
  Tooltip,
  Pagination,
  Snackbar,
  InputAdornment,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faDownload,
  faWallet,
  faArrowUp,
  faArrowDown,
  faRefresh,
  faTimes,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { maskVpa } from '../../utils/maskVpa';
import { formatDateTimeIST } from '../../utils/dateFormat';
// [CASH-MODE] UPI / Cash filter + indication (withdraw rows have no mode)
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import PaymentModeTotals from '../../components/PaymentModeTotals';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam, paymentModeLabel, rowPaymentMode, totalsByModeFromRows } from '../../utils/paymentMode';

const MsbCommissionTransactions = () => {
  const { userId } = useParams(); // Changed from msbId to userId
  const navigate = useNavigate();
  const [userLevel, setUserLevel] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserLevel(decoded.level);
      } catch (err) {}
    }
  }, []);
  
  // Date state
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  
  // Data state
  const [transactions, setTransactions] = useState([]);
  const [msbInfo, setMsbInfo] = useState({});
  const [summary, setSummary] = useState({
    totalBalance: 0,
    dateRange: {
      totalAdd: 0,
      totalWithdraw: 0,
      addCount: 0,
      withdrawCount: 0
    },
    currentPage: {
      pageAddTotal: 0,
      pageWithdrawTotal: 0,
      addCount: 0,
      withdrawCount: 0
    }
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 50;
  
  // Filter state
  const [transactionType, setTransactionType] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Dialog state
  const [withdrawDialog, setWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  
  // NEW: Total Balance Dialog state
  const [totalBalanceDialog, setTotalBalanceDialog] = useState({
    open: false,
    balance: 0,
    msbName: '',
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const { can } = usePermissions();
  const canWithdraw = can('qpayCentre.settlementsHandle.withdraw');
  const canExport = can('qpayCentre.settlementsHandle.export');
  
  // Print ref
  const componentRef = useRef();
  
  // Get current user token for headers
  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      setSnackbar({
        open: true,
        message: 'Authentication required. Please login.',
        severity: 'error'
      });
      navigate('/login');
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setCurrentUser(decodedToken);
    } catch (error) {
      console.error('Error decoding token:', error);
      setSnackbar({
        open: true,
        message: 'Invalid token. Please login again.',
        severity: 'error'
      });
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    if (userId && currentUser) {
      fetchCommissionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentUser, currentPage, startDate, endDate, transactionType, paymentMode]); // [CASH-MODE] paymentMode

  const fetchCommissionData = async () => {
    if (!validateDates()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      const requestData = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        page: currentPage,
        limit: limit,
        ...(transactionType && { transactionType }),
        paymentMode: paymentModeParam(paymentMode) // [CASH-MODE]
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/msbCommission/data/${userId}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200 && response.data.status === 'SUCCESS') {
        const { 
          msbInfo: responseMsbInfo,
          transactions: responseTransactions,
          summary: responseSummary,
          pagination
        } = response.data.data;
        
        setMsbInfo(responseMsbInfo || {});
        // [CASH-MODE] with a mode selected keep only that mode's credit rows (client-side guard)
        const allRows = (responseTransactions && responseTransactions.all) || [];
        setTransactions(paymentModeParam(paymentMode)
          ? allRows.filter((t) => t.method === 'add' && rowPaymentMode(t) === paymentMode)
          : allRows);
        setSummary(responseSummary || {
          totalBalance: 0,
          dateRange: { totalAdd: 0, totalWithdraw: 0, addCount: 0, withdrawCount: 0 },
          currentPage: { pageAddTotal: 0, pageWithdrawTotal: 0, addCount: 0, withdrawCount: 0 }
        });
        
        if (pagination) {
          setTotalPages(pagination.totalPages || 1);
          setTotalRecords(pagination.totalRecords || 0);
        }

        // NEW: Show Total Balance Dialog after successful data fetch
        setTotalBalanceDialog({
          open: true,
          balance: (responseSummary && responseSummary.totalBalance) || 0,
          msbName: (responseMsbInfo && responseMsbInfo.name) || 'MSB User',
        });
        
      } else {
        console.error('Failed to fetch commission data');
        setSnackbar({
          open: true,
          message: 'Failed to fetch commission data',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error response:', (error.response && error.response.data));
      
      if (error.response && error.response.status === 401) {
        setSnackbar({
          open: true,
          message: 'Session expired. Please login again.',
          severity: 'error'
        });
        navigate('/login');
      } else {
        setSnackbar({
          open: true,
          message: (error.response && error.response.data && error.response.data.message) || 'Error fetching commission data',
          severity: 'error'
        });
      }
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!canWithdraw) return;
    if (!validateWithdrawal()) {
      return;
    }
    
    setWithdrawLoading(true);
    
    try {
      const requestData = {
        amount: parseFloat(withdrawAmount),
        reason: withdrawReason,
        adminNotes: adminNotes
      };
      
      const response = await axios.post(
        `${API_BASE_URL}/msbCommission/withdraw/${userId}`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200 && response.data.status === 'SUCCESS') {
        const { withdrawalTransaction, balanceInfo } = response.data.data;
        
        setSnackbar({
          open: true,
          message: `Withdrawal successful! Amount: ₹${balanceInfo.withdrawalAmount.toFixed(2)}`,
          severity: 'success'
        });
        
        // Close dialog and reset form
        setWithdrawDialog(false);
        setWithdrawAmount('');
        setWithdrawReason('');
        setAdminNotes('');
        
        // Refresh data (this will also show the Total Balance dialog)
        await fetchCommissionData();
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      setSnackbar({
        open: true,
        message: (error.response && error.response.data && error.response.data.message) || 'Withdrawal failed',
        severity: 'error'
      });
    } finally {
      setWithdrawLoading(false);
    }
  };

  // NEW: Handle Total Balance Dialog Close
  const handleTotalBalanceDialogClose = () => {
    setTotalBalanceDialog({
      open: false,
      balance: 0,
      msbName: '',
    });
  };

  const validateDates = () => {
    if (!startDate || !endDate) {
      setSnackbar({
        open: true,
        message: 'Please select both start date and end date.',
        severity: 'warning'
      });
      return false;
    }
    
    const diffInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (startDate > endDate) {
      setSnackbar({
        open: true,
        message: 'Start date must be on or before the end date.',
        severity: 'warning'
      });
      return false;
    }
    
    if (diffInDays > 31) {
      setSnackbar({
        open: true,
        message: 'Date range cannot be more than 31 days.',
        severity: 'warning'
      });
      return false;
    }
    
    return true;
  };

  const validateWithdrawal = () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid withdrawal amount.',
        severity: 'warning'
      });
      return false;
    }
    
    if (parseFloat(withdrawAmount) > summary.totalBalance) {
      setSnackbar({
        open: true,
        message: `Withdrawal amount cannot exceed available balance of ₹${summary.totalBalance.toFixed(2)}`,
        severity: 'warning'
      });
      return false;
    }
    
    if (!withdrawReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a reason for withdrawal.',
        severity: 'warning'
      });
      return false;
    }
    
    return true;
  };

  const handlePrint = async () => {
    if (transactions.length === 0) {
      setSnackbar({
        open: true,
        message: 'No commission data to print.',
        severity: 'warning'
      });
      return;
    }

    try {
      const input = componentRef.current;
      const canvas = await html2canvas(input, {
        scale: 2,
        logging: false,
        useCORS: true,
        windowWidth: 1400,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'pt', 'a4');
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      const msbNameSlug = (msbInfo && msbInfo.name) ? msbInfo.name.replace(/\s+/g, '_') : 'MSB';
      pdf.save(`MSB_CommissionTransactions_${msbNameSlug}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: 'Error generating PDF',
        severity: 'error'
      });
    }
  };

  const handleExportCSV = () => {
    if (!canExport) return;
    if (transactions.length === 0) {
      setSnackbar({
        open: true,
        message: 'No data available to export',
        severity: 'warning'
      });
      return;
    }

    const headers = [
      'S.No.',
      'Date & Time',
      'Type',
      'Mode', // [CASH-MODE]
      'Amount',
      'VPA ID',
      'Shop Details',
      'Original Transaction'
    ];

    const rows = transactions.map((t) => [
      t.serialNumber,
      `"${formatDate(t.transactionDate)}"`,
      t.method.toUpperCase(),
      t.method === 'withdraw' ? '-' : paymentModeLabel(t), // [CASH-MODE]
      t.amount || 0,
      `"${displayVpa(t)}"`,
      `"${shopLabel(t.shop)}"`,
      `"${t.originalTransaction ? `${t.originalTransaction.totalAmount} (${t.originalTransaction.commissionPercentage}%)` : '-'}"`
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `qpay_centre_commission_transactions_${userId}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => formatDateTimeIST(date);

  // Cash rows carry an internal ledger key (CASH-<id>) as VPA - never show it
  const displayVpa = (t) => {
    const v = t && t.vpaId;
    if (!v || v === '-' || /^cash-/i.test(String(v)) || (t.method !== 'withdraw' && t.paymentMode === 'CASH')) return '-';
    return maskVpa(v, userLevel);
  };
  const shopLabel = (shop) => (shop ? [shop.businessName, shop.city].filter((x) => x && x !== '-').join(' - ') || '-' : 'Manual Transaction');

  const getTransactionIcon = (method) => {
    return method === 'add' ? faArrowUp : faArrowDown;
  };

  const getTransactionColor = (method) => {
    return method === 'add' ? 'success.main' : 'error.main';
  };

  const columns = [
    { 
      field: 'serialNumber', 
      headerName: 'S.No.', 
      width: 80,
      align: 'center',
      headerAlign: 'center'
    },
    { 
      field: 'transactionDate', 
      headerName: 'Date & Time', 
      width: 160,
      renderCell: (params) => formatDate(params.value)
    },
    { 
      field: 'method', 
      headerName: 'Type', 
      width: 100,
      renderCell: (params) => (
        <Chip
          icon={<FontAwesomeIcon icon={getTransactionIcon(params.value)} />}
          label={params.value.toUpperCase()}
          size="small"
          color={params.value === 'add' ? 'success' : 'error'}
          variant="filled"
        />
      )
    },
    // [CASH-MODE] withdraw rows have no mode
    {
      field: 'paymentMode',
      headerName: 'Mode',
      width: 90,
      renderCell: (params) => (params.row.method === 'withdraw' ? '-' : <PaymentModeChip row={params.row} />)
    },
    { 
      field: 'amount', 
      headerName: 'Amount', 
      width: 130,
      renderCell: (params) => (
        <Typography color={getTransactionColor(params.row.method)} fontWeight="bold">
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    { 
      field: 'vpaId', 
      headerName: 'VPA ID', 
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {displayVpa(params.row)}
        </Typography>
      )
    },
    { 
      field: 'shopName', 
      headerName: 'Shop Details', 
      width: 200,
      renderCell: (params) => {
        const shop = params.row.shop;
        return shop ? (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {shop.businessName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {[shop.city, shop.pincode].filter((x) => x && x !== '-').join(' - ') || '-'}
            </Typography>
            {shop.mobileNumber && shop.mobileNumber !== '-' && (
              <Typography variant="caption" color="text.secondary" display="block">
                {shop.mobileNumber}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Manual Transaction
          </Typography>
        );
      }
    },
    { 
      field: 'originalAmount', 
      headerName: 'Original Transaction', 
      width: 180,
      renderCell: (params) => {
        const original = params.row.originalTransaction;
        return original ? (
          <Box>
            <Typography variant="body2">
              {formatCurrency(original.totalAmount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {original.commissionPercentage}% commission
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      }
    }
  ];

  // [CASH-MODE] prefer API totals, fall back to credit rows on this page
  const apiTotalsByMode = (summary && (summary.totalsByMode || (summary.dateRange && summary.dateRange.totalsByMode))) || null;
  const modeTotals = apiTotalsByMode || totalsByModeFromRows(transactions.filter((t) => t.method === 'add'), (t) => t.amount);
  const modeAmountKey = modeTotals?.UPI?.totalAdd !== undefined ? 'totalAdd' : 'amount';

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading MSB Commission Data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />
      
      {/* Loading Backdrop */}
      <Backdrop 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }} 
        open={loading}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6" align="center">
          Processing MSB Commission Data...
        </Typography>
      </Backdrop>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              <FontAwesomeIcon icon={faWallet} style={{ marginRight: '10px', color: '#1976d2' }} />
              Q-Pay center Commission Transaction
            </Typography>
            
            {canWithdraw && (
              <Tooltip title="Withdraw Commission">
                <Fab
                  color="secondary"
                  size="medium"
                  onClick={() => setWithdrawDialog(true)}
                  disabled={!summary.totalBalance || summary.totalBalance <= 0}
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                </Fab>
              </Tooltip>
            )}
          </Box>

          {/* MSB Info */}
          {msbInfo.name && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                <strong>{msbInfo.name}</strong> ({msbInfo.email}) | 
                Current Balance: <strong>{formatCurrency(msbInfo.totalBalance)}</strong> | 
                Level: {msbInfo.level}
              </Typography>
            </Alert>
          )}

          {/* Filter Controls */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={transactionType}
                  label="Transaction Type"
                  onChange={(e) => setTransactionType(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="add">Add (Credit)</MenuItem>
                  <MenuItem value="withdraw">Withdraw (Debit)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* [CASH-MODE] */}
            <Grid item xs={12} sm={2}>
              <PaymentModeFilter
                value={paymentMode}
                onChange={(v) => { setPaymentMode(v); setCurrentPage(1); }}
                size="medium"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setCurrentPage(1);
                  fetchCommissionData();
                }}
                startIcon={<FontAwesomeIcon icon={faRefresh} />}
                fullWidth
                disabled={loading}
              >
                Refresh
              </Button>
            </Grid>
            {canExport && (
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleExportCSV}
                  startIcon={<FontAwesomeIcon icon={faDownload} />}
                  fullWidth
                  disabled={transactions.length === 0}
                >
                  CSV
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Active Filters */}
          {transactionType && (
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`Type: ${transactionType.toUpperCase()}`}
                color="primary" 
                variant="outlined"
                onDelete={() => setTransactionType('')}
                deleteIcon={<FontAwesomeIcon icon={faTimes} />}
              />
            </Box>
          )}

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    Total Balance
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(summary.totalBalance)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="success.main">
                    Credits (Add)
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(summary.dateRange.totalAdd)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summary.dateRange.addCount} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="error.main">
                    Debits (Withdraw)
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(summary.dateRange.totalWithdraw)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summary.dateRange.withdrawCount} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary">
                    Page Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {transactions.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {totalRecords} total
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* [CASH-MODE] */}
          <PaymentModeTotals
            totals={modeTotals}
            amountKey={modeAmountKey}
            title={apiTotalsByMode ? 'Credits' : 'Credits (this page)'}
            sx={{ mb: 2 }}
          />

          {/* Data Grid */}
          <Paper elevation={3} sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={transactions}
              columns={columns}
              pageSize={limit}
              rowsPerPageOptions={[limit]}
              disableSelectionOnClick
              loading={loading}
              pagination={false}
              hideFooter
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            />
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, page) => setCurrentPage(page)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Empty State */}
          {!loading && transactions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FontAwesomeIcon icon={faWallet} size="3x" style={{ color: '#ccc', marginBottom: '16px' }} />
              <Typography variant="h6" color="text.secondary">
                No commission transactions found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your date range or filters
              </Typography>
            </Box>
          )}
        </div>
      </LocalizationProvider>

      {/* Withdrawal Dialog */}
      <Dialog 
        open={withdrawDialog && canWithdraw} 
        onClose={() => setWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FontAwesomeIcon icon={faMoneyBillWave} />
            Withdraw MSB Commission
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Available Balance:</strong> {formatCurrency(summary.totalBalance)}
              </Typography>
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Withdrawal Amount"
                  type="number"
                  fullWidth
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{
                    min: 0,
                    max: summary.totalBalance,
                    step: 0.01
                  }}
                  helperText={`Maximum: ${formatCurrency(summary.totalBalance)}`}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reason for Withdrawal"
                  fullWidth
                  multiline
                  rows={2}
                  value={withdrawReason}
                  onChange={(e) => setWithdrawReason(e.target.value)}
                  placeholder="e.g., Monthly commission payout, Quarterly settlement..."
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Admin Notes (Optional)"
                  fullWidth
                  multiline
                  rows={2}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Additional notes for this withdrawal..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialog(false)} disabled={withdrawLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleWithdraw} 
            variant="contained" 
            color="error"
            disabled={withdrawLoading || !withdrawAmount || !withdrawReason}
            startIcon={withdrawLoading ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faMoneyBillWave} />}
          >
            {withdrawLoading ? 'Processing...' : 'Confirm Withdrawal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Total Balance Dialog - Clean, Modern, Emoji-Free */}
      <Dialog
        open={totalBalanceDialog.open}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
            color: 'white',
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            p: 1
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(37, 99, 235, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#3b82f6'
              }}
            >
              <FontAwesomeIcon icon={faWallet} size="2x" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#f8fafc' }}>
              Total Balance Summary
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 1, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#94a3b8', fontWeight: 500, mb: 2 }}>
              {totalBalanceDialog.msbName}
            </Typography>
            
            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '1px', color: '#94a3b8', fontWeight: 700, display: 'block' }}>
              Available Balance
            </Typography>
            <Typography variant="h3" sx={{ 
              fontWeight: 800, 
              color: '#38bdf8',
              letterSpacing: '0.5px',
              mt: 0.5,
              mb: 2
            }}>
              {formatCurrency(totalBalanceDialog.balance)}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#4ade80' }}>
              <FontAwesomeIcon icon={faCheckCircle} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Data Synced & Updated Successfully
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleTotalBalanceDialogClose}
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#2563eb',
              color: '#ffffff',
              fontWeight: 600,
              px: 5,
              py: 1.2,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              },
            }}
          >
            Close Summary
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Print Component (Hidden) */}
      <div style={{ position: 'absolute', left: '-10000px', top: 0 }}>
        <div ref={componentRef} style={{ width: '1400px', padding: '20px' }}>
          <Typography variant="h4" align="center">
            Q-Pay Reports
          </Typography>
          <Typography variant="h6" align="center" style={{ marginTop: '10px' }}>
            Q-Pay center Commission Transaction
          </Typography>
          <Typography variant="subtitle1" align="center" style={{ marginBottom: '20px' }}>
            {msbInfo.name} | {`From ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`}
          </Typography>
          
          {/* Print Summary */}
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <Typography variant="h6">Commission Summary</Typography>
            <Typography>
              Balance: {formatCurrency(summary.totalBalance)} | 
              Credits: {formatCurrency(summary.dateRange.totalAdd)} | 
              Debits: {formatCurrency(summary.dateRange.totalWithdraw)}
            </Typography>
          </div>

          {/* Print Table */}
          {transactions.length > 0 && (
            <table className="print-table" border="1" cellPadding="5" style={{ width: '100%', fontSize: '12px' }}>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Date & Time</th>
                  <th>Type</th>
                  <th>Mode</th>{/* [CASH-MODE] */}
                  <th>Amount</th>
                  <th>VPA ID</th>
                  <th>Shop Details</th>
                  <th>Original Transaction</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.serialNumber}</td>
                    <td>{formatDate(transaction.transactionDate)}</td>
                    <td>{transaction.method.toUpperCase()}</td>
                    <td>{transaction.method === 'withdraw' ? '-' : paymentModeLabel(transaction)}</td>{/* [CASH-MODE] */}
                    <td>{formatCurrency(transaction.amount)}</td>
                    <td>{displayVpa(transaction)}</td>
                    <td>
                      {shopLabel(transaction.shop)}
                    </td>
                    <td>
                      {transaction.originalTransaction ? 
                        `${formatCurrency(transaction.originalTransaction.totalAmount)} (${transaction.originalTransaction.commissionPercentage}%)` : 
                        '-'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Box>
  );
};

export default MsbCommissionTransactions;